/**
 * compactDegrade — decides whether the cursor statement is a shape the focused
 * compact editor can safely edit, or whether it should degrade to a rich
 * headline + facts summary + "Open full VQE" call-to-action.
 *
 * The compact surface edits the flat SELECT clause set (columns / WHERE /
 * GROUP BY / HAVING / joins / sort / limit) — optionally behind a `WITH` prefix,
 * which the WITH strip + drill-in own (spec 2026-07-28) — plus the simple
 * WHERE-bearing DML (UPDATE / DELETE), where a `WITH` prefix DOES still degrade
 * because those outline extractors have no WITH handling (v1 scope). Anything
 * else that needs the full structured editor — a main-body
 * UNION/INTERSECT/EXCEPT (Oracle: MINUS), a NAMED window (`OVER w` / a
 * standalone `WINDOW` clause — an inline `OVER (…)` instead locks per-column and
 * stays in the editable path), a derived-table subquery, a GRAPH_TABLE
 * (SQL/PGQ) FROM item, or a heavier DML shape (INSERT, MERGE) — degrades: we render a
 * structure-aware summary (`compactDegradeDetail.ts`'s `buildDegradeDetail`)
 * and route the user to the full Visual Query Editor instead of showing
 * controls that can't faithfully round-trip the statement.
 *
 * Pure logic over `(RenderMode, QueryStructure | null, SqlEngine, InsertOutline
 * | null)` — no React, no enums (string-union `reason` per CLAUDE.md) — so
 * it's unit-testable in isolation.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md § WS3
 *   ("GRACEFUL DEGRADE"); Ralph/VqePolish/advisors/02-dml-safety-and-polish.md
 *   § B2 (richness); Ralph/VqePolish/advisors/03-bug-dossier.md § BUG 2
 *   (Oracle set-operator dialect leak — fixed here via `buildDegradeDetail`).
 */

import type { SqlEngine } from '../../../sql-grammar/model';
import type { QueryStructure } from '../../../sql-resolver/types/queryStructure';
import type { InsertOutline } from '../render/insert/extractInsertOutline';
import type { RenderMode } from '../types';
import {
  buildDegradeDetail,
  hasDerivedTableFrom,
  hasGraphTableFrom,
  hasNamedWindowFunction,
  type DegradeDetail,
} from './compactDegradeDetail';

export type CompactDegradeReason =
  | 'cte'
  | 'set-operator'
  | 'window-function'
  | 'subquery'
  | 'graph-table'
  | 'insert'
  | 'merge'
  | 'unsupported';

export type CompactDegradeResult =
  | { readonly degraded: false }
  | {
      readonly degraded: true;
      readonly reason: CompactDegradeReason;
      /** Headline, or headline + facts newline-joined (`CompactDegradeNotice` splits on `\n`). */
      readonly message: string;
      /** Structured form of `message` — `{ headline, facts }` — for precise assertions/consumers. */
      readonly detail: DegradeDetail;
    };

const NOT_DEGRADED: CompactDegradeResult = { degraded: false };

function degrade(
  reason: CompactDegradeReason,
  structure: QueryStructure | null,
  engine: SqlEngine,
  insertOutline: InsertOutline | null,
  mode: RenderMode | null,
): CompactDegradeResult {
  const detail = buildDegradeDetail(structure, insertOutline, engine, mode);
  const message = detail.facts.length > 0 ? [detail.headline, ...detail.facts].join('\n') : detail.headline;
  return { degraded: true, reason, message, detail };
}

/**
 * Classify the statement for the compact editor. Pass the live `QueryStructure`
 * (from `service.extractQueryStructure`) when available; with a null structure
 * we can only gate on the coarse render mode (INSERT / MERGE / non-DML).
 * `engine` and `insertOutline` feed the richer `detail` (dialect-correct
 * set-operator spelling, INSERT/upsert facts) — both default so existing
 * callers that only care about `reason` keep working unchanged.
 */
export function classifyCompactDegrade(
  mode: RenderMode | null,
  structure: QueryStructure | null,
  engine: SqlEngine = 'postgres',
  insertOutline: InsertOutline | null = null,
): CompactDegradeResult {
  // Heavier DML — gate on render mode even without a structure.
  if (mode === 'insert') return degrade('insert', structure, engine, insertOutline, mode);
  if (mode === 'merge') return degrade('merge', structure, engine, insertOutline, mode);
  // Procedural / parse-error / DDL never reach the compact popover, but be
  // defensive: anything that isn't a compact-editable mode degrades.
  if (mode !== 'select' && mode !== 'update' && mode !== 'delete') {
    return degrade('unsupported', structure, engine, insertOutline, mode);
  }

  if (!structure) return NOT_DEGRADED;

  // CTEs are first-class in the compact SELECT surface (WITH strip + drill-in,
  // spec 2026-07-28), and a CTE body's own set operator / subquery never leaks
  // into the main query's structure — so a `WITH … SELECT` degrades only on
  // what its MAIN body carries. Only the WHERE-bearing DML shapes keep the cte
  // degrade: their outline extractors don't handle a WITH prefix (v1 scope).
  if (mode !== 'select' && (structure.ctes?.length ?? 0) > 0) {
    return degrade('cte', structure, engine, insertOutline, mode);
  }
  if ((structure.compoundBranches?.length ?? 0) > 0) {
    return degrade('set-operator', structure, engine, insertOutline, mode);
  }
  // ANA-13/17/19: only a NAMED window (`OVER w` / standalone `WINDOW` clause)
  // full-degrades — the compact surface has no editable home for it. An INLINE
  // `OVER (…)` window falls through to the normal editable path, where
  // `SelectList` locks just that window chip (per-column) and leaves every other
  // clause editable.
  if (hasNamedWindowFunction(structure)) return degrade('window-function', structure, engine, insertOutline, mode);
  if (mode === 'select' && hasGraphTableFrom(structure)) {
    return degrade('graph-table', structure, engine, insertOutline, mode);
  }
  if (mode === 'select' && hasDerivedTableFrom(structure)) {
    return degrade('subquery', structure, engine, insertOutline, mode);
  }

  return NOT_DEGRADED;
}
